import React, { useState, useRef } from "react";
import { FiX, FiCheck, FiAlertCircle, FiUploadCloud } from "react-icons/fi";
import { apiRequest } from "../utils/apiRequest";
import { useToast } from "./Toast";
import "./MasterUploadModal.css";

const MasterUploadModal = ({ 
  isOpen, 
  onClose, 
  title = "Upload Data", 
  templateUrl, 
  uploadUrl, 
  instructions = [], 
  onSuccess,
  token 
}) => {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef();

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
  };

  const handleUpload = async (e) => {
    if (e) e.preventDefault();
    if (!file) return toast.warning("Please select a file first.");

    setUploading(true);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);

    const res = await apiRequest(uploadUrl, { 
      method: "POST", 
      body: formData,
      isFormData: true 
    }, token);

    setUploading(false);
    if (res?.ok) {
      setResult({ success: true, message: res.data.message, data: res.data });
      if (onSuccess) onSuccess();
    } else {
      setResult({ 
        success: false, 
        message: res?.data?.message || "Upload failed.",
        errors: res?.data?.errors || [] 
      });
    }
  };

  const handleDownloadTemplate = async (e) => {
    e.preventDefault();
    if (!templateUrl) return toast.info("Template URL is not configured for this upload.");
    try {
        const response = await fetch(templateUrl, {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${title.replace(/\s+/g, "_")}_Template.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } else {
            toast.error(`Failed to download template. (URL: ${templateUrl}, Status: ${response.status})`);
        }
    } catch (err) {
        console.error("Template download error:", err);
        toast.error(`Error downloading template: ${err.message}`);
    }
  };

  return (
    <div className="vendor-modal-overlay" onClick={onClose}>
      <div className="vendor-modal" onClick={e => e.stopPropagation()} style={{ width: "90%", maxWidth: "700px" }}>
        {/* Header */}
        <div className="cust-modal-header">
          <span style={{ fontWeight: 600 }}>{title.toUpperCase()}</span>
          <button onClick={onClose} className="cust-modal-close"><FiX size={16} /></button>
        </div>

        <div className="upload-modal-body">
          {/* Instructions Box */}
          <div className="upload-instructions">
            <h4>Instructions to upload:</h4>
            <ul>
              <li>Download the .xlsx template by clicking the link below.</li>
              <li>Remove all sample entries. Update .xlsx file with required data.</li>
              {instructions.map((ins, i) => <li key={i}>{ins}</li>)}
              <li>Select file to upload and submit.</li>
            </ul>
          </div>

          {/* Form Rows */}
          <div className="upload-form-row">
            <div className="upload-form-label">Download Template:</div>
            <div className="upload-form-content">
              <button type="button" onClick={handleDownloadTemplate} className="template-link-btn">
                {title} Template
              </button>
            </div>
          </div>

          <div className="upload-form-row">
            <div className="upload-form-label">Upload File:</div>
            <div className="upload-form-content">
              <div className="file-input-wrapper">
                <input 
                  type="file" 
                  accept=".xlsx, .xls" 
                  onChange={handleFileChange} 
                  ref={fileInputRef}
                  disabled={uploading}
                />
              </div>
            </div>
          </div>

          {/* Results Display */}
          {result && (
            <div className={`upload-results ${result.success ? "success" : "error"}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 600 }}>
                {result.success ? <FiCheck /> : <FiAlertCircle />}
                {result.message}
              </div>
              {result.errors && result.errors.length > 0 && (
                <ul className="error-list">
                  {result.errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Footer Buttons */}
          <div style={{ marginTop: 30, textAlign: "right", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button 
                className="save-btn" 
                onClick={handleUpload} 
                disabled={uploading || !file}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <FiUploadCloud /> {uploading ? "Uploading..." : "Upload"}
            </button>
            <button className="cancel-btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterUploadModal;
